import React from "react";
import Seo from "../components/Seo";
import { LegalLayout, LegalSection } from "../components/LegalLayout";

const PrivacyPolicy = () => {
  return (
    <>
      <Seo
        title="Privacy Policy GDPR — Trattamento dati | OnSports"
        description="Informativa sul trattamento dei dati personali raccolti su weareonsports.com ai sensi del GDPR. OnSports S.r.l., Milano. Diritti dell'interessato e contatti."
        path="/privacy-policy"
      />
      <LegalLayout title="Informativa sul trattamento dei dati personali" lastUpdate="25 maggio 2026 · Versione 2.0">
        <p>
          La presente Informativa descrive le modalità di trattamento dei dati
          personali degli utenti che consultano il sito{" "}
          <strong>www.weareonsports.com</strong>, ai sensi del Regolamento UE
          2016/679 (GDPR) e della normativa italiana applicabile.
        </p>

        <LegalSection title="1. Titolare del trattamento">
          <p>
            Il Titolare del trattamento dei dati è <strong>OnSports S.r.l.</strong>,
            con sede legale in Via Jacopo Dal Verme 7, 20159 Milano (MI), P.IVA
            11367080964.
          </p>
          <p>
            Per qualsiasi richiesta in materia di trattamento dei dati personali,
            è possibile contattare il Titolare scrivendo a:{" "}
            <a className="link-os" href="mailto:privacy@weareonsports.com">
              privacy@weareonsports.com
            </a>
            .
          </p>
        </LegalSection>

        <LegalSection title="2. Tipologie di dati raccolti">
          <p>
            Il Titolare raccoglie e tratta le seguenti categorie di dati
            personali:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Dati di navigazione</strong>: indirizzo IP, tipo di
              browser, sistema operativo, pagine visitate, durata della visita,
              sorgente di traffico. Raccolti automaticamente attraverso cookie e
              strumenti analitici, previo consenso dell'utente.
            </li>
            <li>
              <strong>Dati forniti volontariamente</strong>: nome, cognome,
              indirizzo email, azienda di appartenenza, messaggio, eventuali
              file allegati. Raccolti tramite form di contatto, candidature
              spontanee, iscrizione newsletter.
            </li>
            <li>
              <strong>Dati di identificazione aziendale</strong>: tramite il
              servizio LeadInfo, vengono identificate le aziende (non gli
              individui) che visitano il sito a partire da indirizzi IP business.
              I dati raccolti riguardano l'organizzazione, non la persona fisica.
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="3. Finalità e basi giuridiche del trattamento">
          <p>I dati personali sono trattati per le seguenti finalità:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Erogazione del servizio</strong> (art. 6.1.b GDPR):
              consentire l'utilizzo del sito e rispondere alle richieste inviate
              tramite form di contatto.
            </li>
            <li>
              <strong>Analisi statistica</strong> (art. 6.1.a GDPR – consenso):
              comprendere il comportamento di navigazione attraverso Google
              Analytics 4 e Microsoft Clarity, esclusivamente previo consenso
              espresso dell'utente.
            </li>
            <li>
              <strong>Lead generation B2B</strong> (art. 6.1.a GDPR – consenso):
              identificare le aziende interessate ai servizi OnSports tramite
              LeadInfo, esclusivamente previo consenso espresso.
            </li>
            <li>
              <strong>Obblighi di legge</strong> (art. 6.1.c GDPR): adempimento
              a obblighi fiscali, contabili e normativi.
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="4. Strumenti di terze parti">
          <p>
            Il sito utilizza i seguenti strumenti di terze parti, attivati solo
            previo consenso dell'utente:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Google Analytics 4</strong> (Google Ireland Limited) –
              servizio di analisi statistica con IP anonimizzato. Trasferimento
              dati extra-UE verso gli Stati Uniti sulla base delle Standard
              Contractual Clauses della Commissione Europea e del Data Privacy
              Framework.{" "}
              <a
                className="link-os"
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy Google
              </a>
              .
            </li>
            <li>
              <strong>Microsoft Clarity</strong> (Microsoft Corporation) –
              analisi comportamentale tramite session recording e heatmap.
              Trasferimento dati extra-UE sulla base delle Standard Contractual
              Clauses.{" "}
              <a
                className="link-os"
                href="https://privacy.microsoft.com/privacystatement"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Statement Microsoft
              </a>
              .
            </li>
            <li>
              <strong>LeadInfo</strong> (Leadinfo B.V., Paesi Bassi) –
              identificazione aziendale tramite IP.{" "}
              <a
                className="link-os"
                href="https://www.leadinfo.com/en/privacy-and-cookies/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy LeadInfo
              </a>
              .
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="5. Conservazione dei dati">
          <p>
            I dati personali sono conservati per il tempo strettamente
            necessario alle finalità per cui sono stati raccolti:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Dati di contatto (form, email): fino a 24 mesi dall'ultima
              interazione, salvo necessità di conservazione per obblighi di
              legge.
            </li>
            <li>
              Dati di analisi statistica (GA4, Clarity): durata definita dai
              singoli servizi (max 26 mesi per GA4).
            </li>
            <li>
              Dati fiscali e contabili: 10 anni come previsto dalla normativa
              italiana.
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="6. Diritti dell'interessato">
          <p>Ai sensi degli articoli 15-22 GDPR, l'utente ha diritto di:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Accedere ai propri dati personali e ottenerne una copia.</li>
            <li>Richiedere la rettifica di dati inesatti.</li>
            <li>Richiedere la cancellazione dei dati (diritto all'oblio).</li>
            <li>Limitare il trattamento dei dati.</li>
            <li>Opporsi al trattamento per motivi legittimi.</li>
            <li>Richiedere la portabilità dei dati.</li>
            <li>
              Revocare il consenso in qualsiasi momento (senza pregiudicare la
              liceità del trattamento basato sul consenso prima della revoca).
            </li>
            <li>
              Proporre reclamo all'Autorità Garante per la Protezione dei Dati
              Personali (
              <a
                className="link-os"
                href="https://www.garanteprivacy.it"
                target="_blank"
                rel="noopener noreferrer"
              >
                garanteprivacy.it
              </a>
              ).
            </li>
          </ul>
          <p>
            Per esercitare i propri diritti, l'utente può scrivere a{" "}
            <a className="link-os" href="mailto:privacy@weareonsports.com">
              privacy@weareonsports.com
            </a>
            .
          </p>
        </LegalSection>

        <LegalSection title="7. Modifiche all'Informativa">
          <p>
            La presente Informativa può essere modificata in qualsiasi momento.
            Gli utenti sono invitati a consultarla periodicamente. La versione
            attualmente in vigore è quella pubblicata su questa pagina.
          </p>
        </LegalSection>

        <LegalSection title="8. Cookie">
          <p>
            Per informazioni dettagliate sui cookie utilizzati dal sito,
            consulta la nostra{" "}
            <a className="link-os" href="/cookie-policy">
              Cookie Policy
            </a>
            .
          </p>
        </LegalSection>
      </LegalLayout>
    </>
  );
};

export default PrivacyPolicy;
